/**
 * MS Cart Pair Sync v8
 * ════════════════════
 * Syncs quantity and removal between front products and their Zepto back products.
 * ADDED: Blocks ALL checkout buttons (including iframes) with visual overlay
 *
 * RULES:
 * 1. Delete front → delete matching back (ALWAYS)
 * 2. Delete back → keep front (front stays in cart)
 * 3. Back qty MUST ALWAYS EQUAL front qty (not just ≤)
 * 4. Any qty change on either side syncs to match
 * 5. Checkout buttons BLOCKED until quantities verified correct
 *
 * PAIRING STRATEGY:
 * - Back items: title contains "Custom" AND "Back"
 * - Match by COLOR + SIZE when available
 * - Fallback to COLOR-only if size isn't present
 *
 * SCOPE: Only runs on /cart page.
 */
(function(){
  'use strict';

  if (window.location.pathname !== '/cart') return;

  var DEBUG = false;
  var syncing = false;
  var lastSync = 0;
  var prevQtys = {}; /* key -> qty */
  var checkoutBlocked = false;
  var overlay = null;

  function log(){
    if (!DEBUG) return;
    console.log.apply(console, ['[MS Pair Sync]'].concat(Array.prototype.slice.call(arguments)));
  }

  /* ── Create blocking overlay ── */
  function createOverlay(){
    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.id = 'msCartSyncOverlay';
    overlay.style.cssText = 
      'position: fixed !important;' +
      'inset: 0 !important;' +
      'z-index: 999999 !important;' +
      'background: rgba(0,0,0,0.3) !important;' +
      'display: none !important;' +
      'align-items: center !important;' +
      'justify-content: center !important;' +
      'backdrop-filter: blur(2px) !important;';
    
    var message = document.createElement('div');
    message.style.cssText =
      'background: white !important;' +
      'padding: 24px 32px !important;' +
      'border-radius: 12px !important;' +
      'box-shadow: 0 10px 40px rgba(0,0,0,0.2) !important;' +
      'text-align: center !important;' +
      'max-width: 400px !important;' +
      'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;';
    
    message.innerHTML = 
      '<div style="font-size: 18px; font-weight: 600; color: #1a1a1a; margin-bottom: 8px;">Updating cart...</div>' +
      '<div style="font-size: 14px; color: #666; line-height: 1.5;">Please wait while we sync your customizations</div>' +
      '<div style="margin-top: 16px; width: 40px; height: 40px; border: 3px solid #e5e5e5; border-top-color: #2563eb; border-radius: 50%; animation: spin 0.8s linear infinite; margin-left: auto; margin-right: auto;"></div>' +
      '<style>@keyframes spin { to { transform: rotate(360deg); } }</style>';
    
    overlay.appendChild(message);
    document.body.appendChild(overlay);
    
    return overlay;
  }

  /* ── Disable/Enable Checkout ── */
  function disableCheckout(){
    if (checkoutBlocked) return;
    checkoutBlocked = true;

    // Show overlay
    var ol = createOverlay();
    ol.style.display = 'flex';

    // Disable standard checkout button
    var checkoutButtons = document.querySelectorAll(
      'button[name="checkout"], ' +
      'button[type="submit"][form*="cart"], ' +
      'input[name="checkout"]'
    );

    for (var i = 0; i < checkoutButtons.length; i++) {
      checkoutButtons[i].disabled = true;
      checkoutButtons[i].style.opacity = '0.5';
      checkoutButtons[i].style.cursor = 'not-allowed';
      checkoutButtons[i].setAttribute('data-ms-disabled', 'true');
    }

    // Disable payment button containers (covers iframes)
    var paymentContainers = document.querySelectorAll(
      '.shopify-payment-button, ' +
      '.additional-checkout-buttons, ' +
      '.dynamic-checkout, ' +
      '[data-shopify-buttoncontainer]'
    );

    for (var j = 0; j < paymentContainers.length; j++) {
      paymentContainers[j].style.pointerEvents = 'none';
      paymentContainers[j].style.opacity = '0.5';
      paymentContainers[j].setAttribute('data-ms-disabled', 'true');
    }

    log('Checkout blocked');
  }

  function enableCheckout(){
    if (!checkoutBlocked) return;
    checkoutBlocked = false;

    // Hide overlay
    if (overlay) {
      overlay.style.display = 'none';
    }

    // Re-enable standard checkout buttons
    var disabledButtons = document.querySelectorAll('button[data-ms-disabled="true"], input[data-ms-disabled="true"]');
    
    for (var i = 0; i < disabledButtons.length; i++) {
      disabledButtons[i].disabled = false;
      disabledButtons[i].style.opacity = '';
      disabledButtons[i].style.cursor = '';
      disabledButtons[i].removeAttribute('data-ms-disabled');
    }

    // Re-enable payment button containers
    var disabledContainers = document.querySelectorAll('[data-ms-disabled="true"]');
    
    for (var j = 0; j < disabledContainers.length; j++) {
      disabledContainers[j].style.pointerEvents = '';
      disabledContainers[j].style.opacity = '';
      disabledContainers[j].removeAttribute('data-ms-disabled');
    }

    log('Checkout enabled');
  }

  /* ── Verify quantities are correct ── */
  async function verifyQuantities(){
    try {
      var cart = await getCart();
      var pairs = buildPairs(cart.items);
      
      for (var i = 0; i < pairs.length; i++) {
        var p = pairs[i];
        
        // Orphan back - needs cleanup
        if (!p.front && p.back) return false;
        
        // Both exist - quantities must match
        if (p.front && p.back) {
          if (p.front.quantity !== p.back.quantity) return false;
        }
      }
      
      return true; // All quantities verified correct
    } catch(err) {
      console.error('[MS Pair Sync] Verify error:', err);
      return false;
    }
  }

  /* ── Cart API ── */
  function getCart(){
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function(r){ return r.json(); });
  }

  function changeItem(key, qty){
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function(r){ return r.json(); });
  }

  /* ── Classification ── */
  function isBackItem(item){
    var t = (item.title || '').toLowerCase();
    return t.indexOf('custom') !== -1 && t.indexOf('back') !== -1;
  }

  function norm(v){ return (v || '').toString().toLowerCase().trim(); }

  function getItemColor(item){
    /* From variant_title (front items) */
    if (item.variant_title) {
      var parts = item.variant_title.split('/');
      if (parts.length > 0) {
        var c = norm(parts[0]);
        if (c) return c;
      }
    }

    /* From properties (back items) */
    if (item.properties) {
      var cv = item.properties['Color'] || item.properties['color'] || item.properties['_ms_color'] || '';
      if (cv) return norm(cv);
    }

    /* From options_with_values */
    if (item.options_with_values) {
      for (var i = 0; i < item.options_with_values.length; i++) {
        if (norm(item.options_with_values[i].name) === 'color') {
          return norm(item.options_with_values[i].value);
        }
      }
    }

    return '';
  }

  function getItemSize(item){
    /* From variant_title */
    if (item.variant_title) {
      var parts = item.variant_title.split('/');
      if (parts.length > 1) return norm(parts[1]);
    }

    /* From properties */
    if (item.properties) {
      var sv = item.properties['_ms_size'] || '';
      if (sv) return norm(sv);
    }

    /* From options_with_values */
    if (item.options_with_values) {
      for (var i = 0; i < item.options_with_values.length; i++) {
        if (norm(item.options_with_values[i].name) === 'size') {
          return norm(item.options_with_values[i].value);
        }
      }
    }

    return '';
  }

  function keyCS(color, size){
    return norm(color) + '|' + norm(size);
  }

  /* ── Build pairs ── */
  function buildPairs(items){
    var fronts = [];
    var backs = [];

    for (var i = 0; i < items.length; i++) {
      if (isBackItem(items[i])) backs.push(items[i]);
      else fronts.push(items[i]);
    }

    /* Index backs */
    var backsByCS = {};
    var backsByC  = {};
    for (var b = 0; b < backs.length; b++) {
      var bc = getItemColor(backs[b]);
      var bs = getItemSize(backs[b]);
      if (bc) {
        var k1 = bc;
        (backsByC[k1] = backsByC[k1] || []).push(backs[b]);
        if (bs) {
          var k2 = keyCS(bc, bs);
          (backsByCS[k2] = backsByCS[k2] || []).push(backs[b]);
        }
      }
    }

    var pairs = [];
    var usedBackKeys = {};

    function takeFrom(list){
      if (!list || !list.length) return null;
      for (var i = 0; i < list.length; i++) {
        if (!usedBackKeys[list[i].key]) {
          usedBackKeys[list[i].key] = true;
          return list[i];
        }
      }
      return null;
    }

    for (var f = 0; f < fronts.length; f++) {
      var fc = getItemColor(fronts[f]);
      if (!fc) continue;

      var fs = getItemSize(fronts[f]);
      var matched = null;

      /* Prefer exact Color+Size match */
      if (fs) matched = takeFrom(backsByCS[keyCS(fc, fs)]);
      /* Fallback to Color-only */
      if (!matched) matched = takeFrom(backsByC[fc]);

      if (matched) pairs.push({ front: fronts[f], back: matched });
    }

    /* Unmatched backs = orphans (no matching front) */
    for (var k = 0; k < backs.length; k++) {
      if (!usedBackKeys[backs[k].key]) {
        pairs.push({ front: null, back: backs[k] });
      }
    }

    return pairs;
  }

  /* ── Core sync ── */
  async function reconcile(){
    if (syncing) return;
    if (Date.now() - lastSync < 400) return;
    syncing = true;
    lastSync = Date.now();

    // Block checkout immediately
    disableCheckout();

    try {
      var cart = await getCart();
      var pairs = buildPairs(cart.items);
      var changed = false;

      log('Pairs:', pairs.length);

      for (var i = 0; i < pairs.length; i++) {
        var p = pairs[i];

        /* ── RULE 1: Orphan back (no matching front) → DELETE ── */
        if (!p.front && p.back) {
          log('Orphan back (no front):', p.back.title, '→ removing');
          await changeItem(p.back.key, 0);
          changed = true;
          continue;
        }

        /* ── Front without back: allowed, continue ── */
        if (p.front && !p.back) continue;

        /* ── Both exist: enforce equal quantities ── */
        if (p.front && p.back) {
          var fq = p.front.quantity;
          var bq = p.back.quantity;

          /* Front deleted → delete back */
          if (fq === 0) {
            log('Front removed → removing back');
            await changeItem(p.back.key, 0);
            changed = true;
            continue;
          }

          /* Back deleted → keep front (do nothing) */
          if (bq === 0) {
            log('Back removed → front stays (orphan now, will be cleaned up if needed)');
            continue;
          }

          /* Quantities must match exactly */
          if (fq !== bq) {
            var prevF = prevQtys[p.front.key];
            var prevB = prevQtys[p.back.key];

            var frontChanged = (prevF !== undefined && prevF !== fq);
            var backChanged  = (prevB !== undefined && prevB !== bq);

            if (frontChanged && !backChanged) {
              log('Front changed', prevF, '→', fq, '; syncing back to', fq);
              await changeItem(p.back.key, fq);
              changed = true;
            } else if (backChanged && !frontChanged) {
              log('Back changed', prevB, '→', bq, '; syncing front to', bq);
              await changeItem(p.front.key, bq);
              changed = true;
            } else {
              /* Both changed or first run: sync back to front */
              log('Qty mismatch: front=', fq, 'back=', bq, '→ syncing back to front');
              await changeItem(p.back.key, fq);
              changed = true;
            }
          }
        }
      }

      /* Update previous quantities */
      prevQtys = {};
      for (var j = 0; j < cart.items.length; j++) {
        prevQtys[cart.items[j].key] = cart.items[j].quantity;
      }

      if (changed) {
        log('Cart modified, reloading');
        window.location.reload();
      } else {
        // No changes needed, verify and enable checkout
        var isValid = await verifyQuantities();
        if (isValid) {
          enableCheckout();
        } else {
          // Still mismatched, try again
          log('Quantities still mismatched, retrying...');
          setTimeout(reconcile, 500);
        }
      }
    } catch(err) {
      console.error('[MS Pair Sync] Error:', err);
      // On error, enable checkout after delay to avoid permanent lockout
      setTimeout(enableCheckout, 2000);
    } finally {
      syncing = false;
    }
  }

  /* ── Event listeners ── */
  var reconTimer = null;
  function scheduleReconcile(){
    disableCheckout(); // Block immediately when change detected
    clearTimeout(reconTimer);
    reconTimer = setTimeout(reconcile, 800);
  }

  document.addEventListener('click', function(e){
    var btn = e.target.closest ? e.target.closest('button') : null;
    if (!btn) return;
    var name = btn.getAttribute('name');
    if (name === 'plus' || name === 'minus' || name === 'remove') scheduleReconcile();
    if (btn.closest && btn.closest('cart-remove-button')) scheduleReconcile();
  });

  document.addEventListener('change', function(e){
    if (e.target && e.target.tagName === 'INPUT' && e.target.name === 'updates[]') {
      scheduleReconcile();
    }
  });

  var observer = new MutationObserver(function(mutations){
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].type !== 'childList' || !mutations[i].addedNodes.length) continue;
      for (var j = 0; j < mutations[i].addedNodes.length; j++) {
        var node = mutations[i].addedNodes[j];
        if (node.nodeType === 1 && (
          node.id === 'cart' ||
          (node.classList && node.classList.contains('cart-items')) ||
          (node.id && node.id.indexOf('shopify-section-') === 0)
        )) {
          scheduleReconcile();
          return;
        }
      }
    }
  });

  var mainContent = document.getElementById('MainContent') || document.body;
  observer.observe(mainContent, { childList: true, subtree: true });

  window.addEventListener('load', function(){
    disableCheckout(); // Block on load
    getCart().then(function(cart){
      for (var i = 0; i < cart.items.length; i++) {
        prevQtys[cart.items[i].key] = cart.items[i].quantity;
      }
      setTimeout(reconcile, 500);
    });
  });

  // Also apply block immediately on script load
  setTimeout(function(){
    disableCheckout();
  }, 100);

  log('MS Pair Sync v8 initialized (full-screen overlay protection enabled)');
})();
